import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/api/client';
import { recipesApi }    from '@/api/recipes';
import { ingredientsApi } from '@/api/ingredients';
import { useToast }      from '@/providers/ToastProvider';
import { useCurrencyFormatter } from '../../providers/AppSettingsProvider';
import {
  PageHeader, Button, FormField, Input, Textarea, Select, Card,
  CardHeader, CardTitle, CardContent, Spinner, Tabs,
} from '@/components/ui';
import { ArrowLeft, Plus, Trash2, Thermometer } from 'lucide-react';
import { formatNumberedSteps, formatPct } from '@/lib/utils';
import {
  recipeCategoryOptions,
  recipeSubcategoryOptions,
  getRecipeCategoryLabel,
  getRecipeSubcategoryLabel,
} from '@/lib/recipeTaxonomy';
import type { RecipeCalculationResult } from '@/types';
import type { RecipePayload } from '@/api/recipes';
import { getTechnicalTooltipMap } from './technicalTooltips';
import RecipeMediaUpload from '@/components/RecipeMediaUpload';
import { useAuth } from '@/providers/AuthProvider';

const RECIPE_TAX_RATE_OPTIONS = [
  { value: 0, label: '0%' },
  { value: 0.04, label: '4%' },
  { value: 0.1, label: '10%' },
  { value: 0.21, label: '21%' },
] as const;

type RecipeTaxRate = (typeof RECIPE_TAX_RATE_OPTIONS)[number]['value'];

function normalizeRecipeTaxRate(value: number | null | undefined): RecipeTaxRate {
  return RECIPE_TAX_RATE_OPTIONS.some(option => option.value === value)
    ? (value as RecipeTaxRate)
    : 0.1;
}

// ─── Zod schema ────────────────────────────────────────────────────────────────
// ingredient_id holds a prefixed value: "i:<uuid>" for regular ingredients
// or "p:<uuid>" for preparation recipes.
const lineSchema = z.object({
  ingredient_id:  z.string().min(1, 'required'),   // "i:..." or "p:..."
  grams_per_kilo: z.coerce.number().min(0, 'must be ≥ 0'),
  waste_ratio:    z.coerce.number().min(0).max(1, 'must be 0–1'),
  sort_order:     z.coerce.number().int(),
});

const formSchema = z.object({
  name:                 z.string().min(1, 'required'),
  recipe_type_id:       z.string().nullable().optional(),
  recipe_category:      z.enum(['gelato', 'postre', 'salado']).nullable().optional(),
  recipe_subcategory:   z.enum(['crema_blanca', 'crema_manchada', 'sorbete_fruta', 'chocolate_fruto_seco']).nullable().optional(),
  portion_weight_g:     z.coerce.number().positive('must be > 0'),
  multiplier:           z.coerce.number().positive('must be > 0'),
  overrun_pct:          z.coerce.number().min(0),
  tax_rate:             z.union([z.literal(0), z.literal(0.04), z.literal(0.1), z.literal(0.21)]),
  desired_margin:       z.coerce.number().min(0).max(0.9999),
  selling_price:        z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().positive().nullable()),
  prep_time_min:        z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().nonnegative().nullable()),
  cook_time_min:        z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().nonnegative().nullable()),
  preparation_notes:    z.string().nullable().optional(),
  presentation_notes:   z.string().nullable().optional(),
  kitchen_notes:        z.string().nullable().optional(),
  allergens:            z.array(z.string().uuid()).default([]),
  ingredients:          z.array(lineSchema).min(1, 'add at least one ingredient'),
});

type FormValues = z.infer<typeof formSchema>;

// ─── Component ─────────────────────────────────────────────────────────────────
export default function RecipeFormPage() {
  const { t }          = useTranslation();
  const { id }         = useParams<{ id: string }>();
  const navigate        = useNavigate();
  const queryClient     = useQueryClient();
  const { toast }  = useToast();
  const formatMoney = useCurrencyFormatter();
  const isEdit          = !!id;
  const [customAllergenName, setCustomAllergenName] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'pricing' | 'ingredients' | 'notes' | 'media'>('details');
  const { user } = useAuth();

  // ── Remote data ──────────────────────────────────────────────────────────────
  const { data: existing, isLoading: loadingRecipe } = useQuery({
    queryKey: ['recipe', id],
    queryFn:  () => recipesApi.get(id!),
    enabled:  isEdit,
  });

  const { data: ingredientOptions = [] } = useQuery({
    queryKey: ['ingredients-dropdown'],
    queryFn:  ingredientsApi.dropdown,
  });

  const { data: preparationOptions = [] } = useQuery({
    queryKey: ['preparations-dropdown'],
    queryFn:  recipesApi.preparationsDropdown,
  });

  const { data: allergenOptions = [] } = useQuery({
    queryKey: ['recipe-allergens'],
    queryFn:  recipesApi.allergens,
  });


  const { data: recipeTypes = [] } = useQuery({
    queryKey: ['recipe-types'],
    queryFn:  recipesApi.listTypes,
  });

  // ── RHF setup ────────────────────────────────────────────────────────────────
  const {
    register, control, handleSubmit, reset, watch, getValues, setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '', recipe_type_id: null, recipe_category: null, recipe_subcategory: null,
      portion_weight_g: 100, multiplier: 1, overrun_pct: 0,
      tax_rate: 0.10, desired_margin: 0.35, selling_price: null,
      prep_time_min: null, cook_time_min: null,
      preparation_notes: '', presentation_notes: '', kitchen_notes: '',
      allergens: [],
      ingredients: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'ingredients' });
  const preparationNotesRef = useRef<HTMLTextAreaElement | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (existing) {
      reset({
        name:               existing.name,
        recipe_type_id:     existing.recipe_type_id ?? null,
        recipe_category:    existing.recipe_category ?? null,
        recipe_subcategory: existing.recipe_subcategory ?? null,
        portion_weight_g:   Number(existing.portion_weight_g),
        multiplier:         Number(existing.multiplier),
        overrun_pct:        Number(existing.overrun_pct),
        tax_rate:           normalizeRecipeTaxRate(Number(existing.tax_rate)),
        desired_margin:     Number(existing.desired_margin),
        selling_price:      existing.selling_price != null ? Number(existing.selling_price) : null,
        prep_time_min:      existing.prep_time_min ?? null,
        cook_time_min:      existing.cook_time_min ?? null,
        preparation_notes:  existing.preparation_notes ?? '',
        presentation_notes: existing.presentation_notes ?? '',
        kitchen_notes:      existing.kitchen_notes ?? '',
        allergens:          existing.allergens?.map(allergen => allergen.id) ?? [],
        ingredients: existing.ingredients.map((l, i) => ({
          // Build prefixed ID: prep_recipe_id lines use "p:", ingredient_id lines use "i:"
          ingredient_id:  l.prep_recipe_id
            ? `p:${l.prep_recipe_id}`
            : `i:${l.ingredient_id}`,
          grams_per_kilo: Number(l.grams_per_kilo),
          waste_ratio:    Number(l.waste_ratio),
          sort_order:     l.sort_order ?? i,
        })),
      });
    }
  }, [existing, reset]);

  // ── Live preview calculation ──────────────────────────────────────────────────
  const [preview, setPreview] = useState<RecipeCalculationResult | null>(
    existing?.calculations ?? null
  );

  const watchedLines = watch('ingredients');
  const watchPortion = watch('portion_weight_g');
  const watchMult    = watch('multiplier');
  const watchOverrun = watch('overrun_pct');
  const watchTax     = watch('tax_rate');
  const watchMargin  = watch('desired_margin');
  const watchPrice   = watch('selling_price');
  const watchPrep    = watch('prep_time_min');
  const selectedAllergens = watch('allergens') ?? [];

  // Determine if the selected recipe type is a Preparation
  const watchTypeId    = watch('recipe_type_id');
  const watchCategory  = watch('recipe_category');
  const isPreparation  = recipeTypes.find(rt => rt.id === watchTypeId)?.is_preparation ?? false;
  const technicalTooltips = getTechnicalTooltipMap(t);

  // Re-calculate whenever something relevant changes
  const [calcLoading, setCalcLoading] = useState(false);
  useEffect(() => {
    const lines = watchedLines;
    if (!lines || lines.length === 0) { setPreview(null); return; }
    const allFilled = lines.every(l => l.ingredient_id && l.grams_per_kilo >= 0);
    if (!allFilled) return;

    setCalcLoading(true);
    const payload = {
      portion_weight_g:  watchPortion,
      multiplier:        watchMult,
      overrun_pct:       watchOverrun,
      tax_rate:          watchTax,
      desired_margin:    watchMargin,
      selling_price:     watchPrice ?? undefined,
      prep_time_min:     watchPrep  ?? undefined,
      ingredients:       lines.map(l => {
        const isPrepLine = l.ingredient_id.startsWith('p:');
        return {
          ingredient_id:  isPrepLine ? null : l.ingredient_id.replace(/^i:/, ''),
          prep_recipe_id: isPrepLine ? l.ingredient_id.slice(2) : null,
          grams_per_kilo: l.grams_per_kilo,
          waste_ratio:    l.waste_ratio,
        };
      }),
    };
    recipesApi.preview(payload)
      .then(calc => setPreview(calc))
      .catch(() => {/* ignore preview errors */})
      .finally(() => setCalcLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(watchedLines),
    watchPortion, watchMult, watchOverrun, watchTax, watchMargin, watchPrice, watchPrep,
  ]);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: recipesApi.create,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast(t('recipes.created'), 'success');
      navigate(`/recipes/${r.id}`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: RecipePayload }) => recipesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', id] });
      toast(t('recipes.updated'), 'success');
      navigate(`/recipes/${id}`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const createAllergenMutation = useMutation({
    mutationFn: recipesApi.createAllergen,
    onSuccess: (allergen) => {
      queryClient.invalidateQueries({ queryKey: ['recipe-allergens'] });
      const current = getValues('allergens') ?? [];
      if (!current.includes(allergen.id)) {
        setValue('allergens', [...current, allergen.id], {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      setCustomAllergenName('');
      toast(t('recipes.allergenSaved'), 'success');
    },
    onError: (error) => toast(getErrorMessage(error), 'error'),
  });

  const onSubmit = (data: FormValues) => {
    // Transform prefixed ingredient_id values into ingredient_id / prep_recipe_id fields
    const ingredients = data.ingredients.map(l => {
      const isPrepLine = l.ingredient_id.startsWith('p:');
      return {
        ingredient_id:  isPrepLine ? null : l.ingredient_id.replace(/^i:/, ''),
        prep_recipe_id: isPrepLine ? l.ingredient_id.slice(2) : null,
        grams_per_kilo: l.grams_per_kilo,
        waste_ratio:    l.waste_ratio,
        sort_order:     l.sort_order,
      };
    });
    const payload = { ...data, ingredients };
    if (isEdit) {
      updateMutation.mutate({ id: id!, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const addLine = () =>
    append({ ingredient_id: '', grams_per_kilo: 0, waste_ratio: 0, sort_order: fields.length });

  const toggleAllergen = (allergenId: string, checked: boolean) => {
    const current = getValues('allergens') ?? [];
    const next = checked
      ? [...new Set([...current, allergenId])]
      : current.filter(id => id !== allergenId);

    setValue('allergens', next, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleCreateAllergen = () => {
    const nextName = customAllergenName.trim();
    if (!nextName) return;
    createAllergenMutation.mutate(nextName);
  };

  const handlePreparationNotesChange = (value: string) => {
    setValue('preparation_notes', formatNumberedSteps(value), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handlePreparationNotesEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();

    const textarea = event.currentTarget;
    const nextValue = `${textarea.value.slice(0, textarea.selectionStart)}\n${textarea.value.slice(textarea.selectionEnd)}`;
    const formattedValue = formatNumberedSteps(nextValue, true);
    const nextCursor = formattedValue.length;

    setValue('preparation_notes', formattedValue, {
      shouldDirty: true,
      shouldTouch: true,
    });

    window.requestAnimationFrame(() => {
      preparationNotesRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (isEdit && loadingRecipe) return <Spinner className="py-20" />;

  const hasDetailsError = !!(errors.name || errors.portion_weight_g || errors.overrun_pct || errors.prep_time_min || errors.cook_time_min || errors.recipe_category || errors.recipe_subcategory || errors.recipe_type_id);
  const hasPricingError = !!(errors.tax_rate || errors.desired_margin || errors.selling_price);
  const hasIngredientsError = !!(errors.ingredients);

  const formTabs = [
    { key: 'details',     label: t('common.details'),        error: hasDetailsError },
    { key: 'pricing',     label: t('recipes.pricing'),       error: hasPricingError },
    { key: 'ingredients', label: t('recipes.ingredients'),   error: hasIngredientsError },
    { key: 'notes',       label: t('recipes.allergens') + ' & Notes' },
    ...(isEdit ? [{ key: 'media', label: t('media.tabLabel') }] : []),
  ] as const;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="pt-4 md:pt-6 lg:pt-8">
      <PageHeader
        title={isEdit ? t('recipes.editRecipe') : t('recipes.newRecipe')}
        subtitle={existing?.name}
      >
        <Button asChild variant="outline" size="sm">
          <Link to={isEdit ? `/recipes/${id}` : '/recipes'}><ArrowLeft size={15} /> {t('common.back')}</Link>
        </Button>
      </PageHeader>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

          {/* ── Tabbed main column ───────────────────────────────────────────── */}
          <div className="lg:col-span-2">
            <Card>
              <Tabs
                tabs={formTabs as unknown as { key: string; label: string; error?: boolean }[]}
                active={activeTab}
                onChange={k => setActiveTab(k as typeof activeTab)}
                className="px-5"
              />

              <CardContent className="pt-5 space-y-4">

                {/* ── Details tab ─────────────────────────────────────────── */}
                {activeTab === 'details' && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <FormField label={t('common.name')} error={errors.name?.message} required>
                        <Input {...register('name')} placeholder={t('recipes.namePlaceholder')} />
                      </FormField>
                    </div>
                    <FormField label={t('recipes.category')} error={(errors.recipe_category as any)?.message}>
                      <Controller
                        name="recipe_category"
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value ?? ''} onChange={e => field.onChange(e.target.value || null)}>
                            <option value="">{t('common.selectOptional')}</option>
                            {recipeCategoryOptions.map(option => (
                              <option key={option} value={option}>{getRecipeCategoryLabel(t, option)}</option>
                            ))}
                          </Select>
                        )}
                      />
                    </FormField>
                    <FormField label={t('recipes.subcategory')} error={(errors.recipe_subcategory as any)?.message}>
                      <Controller
                        name="recipe_subcategory"
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value ?? ''} onChange={e => field.onChange(e.target.value || null)}>
                            <option value="">{t('common.selectOptional')}</option>
                            {recipeSubcategoryOptions.map(option => (
                              <option key={option} value={option}>{getRecipeSubcategoryLabel(t, option)}</option>
                            ))}
                          </Select>
                        )}
                      />
                    </FormField>
                    <FormField label={t('recipes.type')} error={(errors.recipe_type_id as any)?.message}>
                      <Controller
                        name="recipe_type_id"
                        control={control}
                        render={({ field }) => (
                          <Select value={field.value ?? ''} onChange={e => field.onChange(e.target.value || null)}>
                            <option value="">{t('common.selectOptional')}</option>
                            {recipeTypes.map(rt => (
                              <option key={rt.id} value={rt.id}>{rt.name}</option>
                            ))}
                          </Select>
                        )}
                      />
                      {isPreparation && (
                        <p className="mt-1 text-xs text-blue-600 font-medium">{t('recipes.isPreparationNote')}</p>
                      )}
                    </FormField>
                    <FormField label={t('recipes.portionWeight')} error={errors.portion_weight_g?.message} required>
                      <Input type="number" step="0.01" {...register('portion_weight_g')} placeholder="100" />
                    </FormField>
                    <FormField label={t('recipes.overrunPct')} error={errors.overrun_pct?.message} required>
                      <Input type="number" step="0.1" {...register('overrun_pct')} placeholder="0" />
                    </FormField>
                    <FormField label={t('recipes.prepTime')} error={errors.prep_time_min?.message}>
                      <Input type="number" step="1" {...register('prep_time_min')} placeholder="—" />
                    </FormField>
                    <FormField label={t('recipes.cookTime')} error={errors.cook_time_min?.message}>
                      <Input type="number" step="1" {...register('cook_time_min')} placeholder="—" />
                    </FormField>
                  </div>
                )}

                {/* ── Pricing tab ─────────────────────────────────────────── */}
                {activeTab === 'pricing' && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField label={t('recipes.taxRate')} error={errors.tax_rate?.message} required>
                      <Controller
                        name="tax_rate"
                        control={control}
                        render={({ field }) => (
                          <Select value={String(field.value)} onChange={event => field.onChange(Number(event.target.value))}>
                            {RECIPE_TAX_RATE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Select>
                        )}
                      />
                    </FormField>
                    <FormField label={t('recipes.desiredMargin')} error={errors.desired_margin?.message} required>
                      <Controller
                        name="desired_margin"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="number" step="1" min="0" max="99.99" placeholder="35"
                            value={field.value != null ? +(field.value * 100).toFixed(4) : ''}
                            onChange={e => field.onChange(e.target.value === '' ? 0 : Number(e.target.value) / 100)}
                          />
                        )}
                      />
                    </FormField>
                    <FormField label={t('recipes.sellingPrice')} error={(errors.selling_price as any)?.message}>
                      <Input type="number" step="0.01" {...register('selling_price')} placeholder="—" />
                    </FormField>
                    <FormField label={t('recipes.recommendedPrice')}>
                      <Input
                        type="text" readOnly
                        value={preview ? formatMoney(preview.recommended_price) : '—'}
                        className="bg-muted cursor-default"
                      />
                    </FormField>
                  </div>
                )}

                {/* ── Ingredients tab ─────────────────────────────────────── */}
                {activeTab === 'ingredients' && (
                  <div className="space-y-3">
                    <div className="flex justify-end">
                      <Button type="button" size="sm" variant="outline" onClick={addLine}>
                        <Plus size={14} /> {t('recipes.addIngredient')}
                      </Button>
                    </div>
                    {errors.ingredients?.root && (
                      <p className="text-sm text-destructive">{errors.ingredients.root.message}</p>
                    )}
                    {fields.length > 0 && (
                      <div className="hidden sm:grid sm:grid-cols-[1fr_120px_100px_36px] sm:gap-2 px-1 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                        <span>{t('ingredients.name')}</span>
                        <span>{t('recipes.gramsPerKilo')}</span>
                        <span>{t('recipes.wasteRatio')}</span>
                        <span />
                      </div>
                    )}
                    {fields.map((field, idx) => (
                      <div key={field.id} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_120px_100px_36px] sm:items-start border rounded-md p-2">
                        <Controller
                          name={`ingredients.${idx}.ingredient_id`}
                          control={control}
                          render={({ field: f }) => (
                            <div>
                              <Select value={f.value} onChange={f.onChange} className={errors.ingredients?.[idx]?.ingredient_id ? 'border-destructive' : ''}>
                                <option value="">{t('ingredients.select')}</option>
                                {ingredientOptions.length > 0 && (
                                  <optgroup label={t('nav.ingredients')}>
                                    {ingredientOptions.map(opt => (
                                      <option key={opt.id} value={`i:${opt.id}`}>{opt.name}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {preparationOptions.length > 0 && (
                                  <optgroup label={t('recipes.preparationSource')}>
                                    {preparationOptions.map(opt => (
                                      <option key={opt.id} value={`p:${opt.id}`}>{opt.name}</option>
                                    ))}
                                  </optgroup>
                                )}
                              </Select>
                              {errors.ingredients?.[idx]?.ingredient_id && (
                                <p className="mt-1 text-xs text-destructive">{errors.ingredients[idx]!.ingredient_id?.message}</p>
                              )}
                            </div>
                          )}
                        />
                        <div>
                          <Controller
                            name={`ingredients.${idx}.grams_per_kilo`}
                            control={control}
                            render={({ field: f }) => (
                              <Input type="number" step="0.01" min="0" placeholder="0" value={f.value} onChange={f.onChange}
                                className={errors.ingredients?.[idx]?.grams_per_kilo ? 'border-destructive' : ''} />
                            )}
                          />
                          {errors.ingredients?.[idx]?.grams_per_kilo && (
                            <p className="mt-1 text-xs text-destructive">{errors.ingredients[idx]!.grams_per_kilo?.message}</p>
                          )}
                        </div>
                        <Controller
                          name={`ingredients.${idx}.waste_ratio`}
                          control={control}
                          render={({ field: f }) => (
                            <Input type="number" step="0.001" min="0" max="1" placeholder="0" value={f.value} onChange={f.onChange} />
                          )}
                        />
                        <Button type="button" variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={() => remove(idx)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                    {fields.length === 0 && (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        {t('recipes.noIngredients')} — <button type="button" className="underline text-primary" onClick={addLine}>{t('recipes.addFirst')}</button>
                      </p>
                    )}
                    {fields.length > 0 && (() => {
                      const vals = getValues('ingredients');
                      const total = vals.reduce((s, l) => s + (Number(l.grams_per_kilo) || 0), 0);
                      const diff  = Math.abs(total - 1000);
                      return (
                        <div className={`flex items-center justify-between text-sm rounded px-3 py-1.5 ${diff < 1 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          <span>{t('recipes.recipeBase')}</span>
                          <span className="font-mono font-semibold">{total.toFixed(2)} g / 1000 g</span>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* ── Allergens & Notes tab ────────────────────────────────── */}
                {activeTab === 'notes' && (
                  <div className="space-y-6">
                    <div>
                      <CardTitle className="mb-3 text-sm">{t('recipes.allergens')}</CardTitle>
                      {allergenOptions.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {allergenOptions.map(allergen => {
                            const checked = selectedAllergens.includes(allergen.id);
                            return (
                              <label key={allergen.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm transition-colors hover:bg-muted/40">
                                <input type="checkbox" checked={checked}
                                  onChange={event => toggleAllergen(allergen.id, event.target.checked)}
                                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
                                <span className="flex-1">{allergen.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('recipes.allergensEmpty')}</p>
                      )}
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <Input value={customAllergenName} onChange={event => setCustomAllergenName(event.target.value)}
                          placeholder={t('recipes.customAllergenPlaceholder')}
                          onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); handleCreateAllergen(); } }} />
                        <Button type="button" variant="outline" onClick={handleCreateAllergen}
                          disabled={!customAllergenName.trim() || createAllergenMutation.isPending}>
                          <Plus size={14} /> {t('recipes.addCustomAllergen')}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <CardTitle className="text-sm">Notes</CardTitle>
                      <FormField label={t('recipes.preparationNotes')}>
                        <Controller
                          name="preparation_notes"
                          control={control}
                          render={({ field }) => (
                            <Textarea rows={4}
                              ref={node => { field.ref(node); preparationNotesRef.current = node; }}
                              value={field.value ?? ''} onBlur={field.onBlur}
                              onChange={event => handlePreparationNotesChange(event.target.value)}
                              onKeyDown={handlePreparationNotesEnter}
                              placeholder={t('recipes.preparationNotesHint')} />
                          )}
                        />
                      </FormField>
                      <FormField label={t('recipes.presentationNotes')}>
                        <Textarea rows={3} {...register('presentation_notes')} />
                      </FormField>
                      <FormField label={t('recipes.kitchenNotes')}>
                        <Textarea rows={2} {...register('kitchen_notes')} placeholder={t('recipes.kitchenNotesHint')} />
                      </FormField>
                    </div>
                  </div>
                )}

                {/* ── Media tab ───────────────────────────────────────── */}
                {activeTab === 'media' && isEdit && (
                  <RecipeMediaUpload
                    recipeId={id!}
                    canDelete={user?.role === 'admin'}
                  />
                )}

              </CardContent>
            </Card>
          </div>

          {/* ── Calculation summary sidebar ─────────────────────────────────── */}
          <div className="space-y-4">
            <Card className="sticky top-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('recipes.livePreview')}</CardTitle>
                  {calcLoading && <Spinner size="sm" />}
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pb-2">
                {preview ? (
                  <>
                    <Section title={t('recipes.production')}>
                      <Row label={t('recipes.totalProduction')} value={`${preview.total_production_grams.toFixed(0)} g`} />
                      <Row label={t('recipes.portions')}         value={String(preview.number_of_portions)} />
                    </Section>

                    {watchCategory === 'gelato' && (
                      <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                        preview.suggested_serving_temp === 'PAC ERROR'
                          ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
                          : 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                      }`}>
                        <Thermometer size={16} />
                        <span>{t('recipes.servingTemp')}:</span>
                        <span className="ml-auto font-bold">{preview.suggested_serving_temp}</span>
                      </div>
                    )}

                    <Section title={t('recipes.technicals')}>
                      <Row label={t('recipes.totalSugars')}  value={formatPct(preview.total_sugars_pct)} tooltip={technicalTooltips.sugars} warn={preview.total_sugars_pct > 26} />
                      <Row label={t('recipes.totalFat')}     value={formatPct(preview.total_fat_pct)} tooltip={technicalTooltips.fat} />
                      <Row label={t('recipes.totalSML')}     value={formatPct(preview.total_sml_pct)} tooltip={technicalTooltips.sml} />
                      <Row label="Solids (non-fat)"          value={formatPct(preview.total_solids_diff)} />
                      <Row label={t('recipes.totalSolids')}  value={formatPct(preview.total_solids_total)} tooltip={technicalTooltips.totalSolids} />
                      {watchCategory === 'gelato' && <Row label={t('recipes.totalPOD')} value={String(preview.total_pod)} />}
                      {watchCategory === 'gelato' && <Row label={t('recipes.totalPAC')} value={String(preview.total_pac)} tooltip={technicalTooltips.pac} bold />}
                    </Section>

                    <Section title={t('recipes.financial')}>
                      <Row label={t('recipes.costPer1000g')}   value={formatMoney(preview.total_cost_per_1000g)} />
                      {preview.labor_cost > 0 && (
                        <Row label={t('recipes.laborCost')} value={formatMoney(preview.labor_cost)} />
                      )}
                      <Row label={t('recipes.costPerPortion')} value={formatMoney(preview.cost_per_portion)} bold />
                      <Row label={t('recipes.recommendedPrice')} value={formatMoney(preview.recommended_price)} />
                      {watchPrice && (
                        <Row label={t('recipes.actualMargin')} value={formatPct(preview.actual_margin_pct)} />
                      )}
                    </Section>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    {fields.length === 0 ? t('recipes.addIngredientToPreview') : t('recipes.fillIngredients')}
                  </p>
                )}
              </CardContent>
              <div className="border-t px-6 py-4 space-y-2">
                <Button type="submit" className="w-full" disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}>
                  {isSubmitting || createMutation.isPending || updateMutation.isPending
                    ? t('common.saving')
                    : isEdit ? t('common.saveChanges') : t('recipes.createRecipe')}
                </Button>
                <Button type="button" variant="outline" className="w-full" asChild>
                  <Link to={isEdit ? `/recipes/${id}` : '/recipes'}>{t('common.cancel')}</Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}

// ─── Small helper components ──────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, bold, warn, tooltip }: { label: string; value: string; bold?: boolean; warn?: boolean; tooltip?: string }) {
  return (
    <div className={`flex items-center justify-between text-sm ${warn ? 'text-amber-600' : ''}`}>
      <span className="text-muted-foreground">{label}</span>
      <span
        className={tooltip ? `cursor-help decoration-dotted underline-offset-4 ${bold ? 'font-semibold underline' : 'underline'}` : bold ? 'font-semibold' : ''}
        title={tooltip}
      >
        {value}
      </span>
    </div>
  );
}
